import React, { useRef, useState } from 'react';
import styles from './DocumentPage.module.scss';
import { Links } from 'application/constants/links';
import { VariableList } from '../variables/VariablesList';
import { lazyRetry } from 'helpers/retry-helper';
import { UrlType } from 'services/api/api-client.types';
import { ProjectProvider } from 'helpers/projectPermissions/ProjectContext';
import { FabButton } from 'components/uikit/buttons/FabButton.tsx';
import { GenerateDescriptionChat } from 'helpers/AI/components/generateDescriptionChat/GenerateDescriptionChat.tsx';
import ChatIcon from 'assets/icons/chat_40.svg?react';
import { DocumentVariable } from './components/richedit-helper.ts';
import { FeatureFlags } from 'application/constants/env-variables.ts';

const DocumentEditor = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "document-editor" */ 'components/documents/editors/DocumentEditor'
    ),
);

export const DocumentPage: React.FC = () => {
  const params = Links.Authorized.OpenDocument.useParams();
  const { queryParams } = Links.Authorized.CreateDocument.useParams();
  const insertVariable = useRef<
    (variableId: number, urlType: UrlType | null) => void
  >(null!);
  const updateVariable = useRef<
    (variableId: number[], disableRefetch: boolean) => Promise<void>
  >(null!);
  const [isEditingLocked, setIsEditingLocked] = useState(false);
  const [generateDescriptionState, setGenerateDescriptionState] = useState<{
    documentVariable?: DocumentVariable;
    isOpen: boolean;
  }>({ isOpen: false });
  const onGenerateDescriptionClick = (documentVariable: DocumentVariable) => {
    setGenerateDescriptionState({ documentVariable, isOpen: true });
  };

  return (
    <ProjectProvider projectId={params.projectId}>
      <div className={styles.container} data-test-id="document-page">
        <DocumentEditor
          projectId={params.projectId}
          revisionId={params.releaseId}
          documentId={params.documentId}
          folderId={queryParams.folderId}
          insertVariable={insertVariable}
          updateVariable={updateVariable}
          setIsEditingLocked={setIsEditingLocked}
          onGenerateDescriptionClick={onGenerateDescriptionClick}
        />
        <div className={styles.variablesContainer}>
          <VariableList
            projectId={params.projectId}
            revisionId={params.releaseId}
            isDocumentEditingLocked={isEditingLocked}
            onVariableUpdate={async (
              variableId: number[],
              disableRefetch: boolean,
            ) => {
              await updateVariable.current?.(variableId, disableRefetch);
            }}
            onVariableClick={(id, urlType) => {
              insertVariable.current?.(id, urlType);
            }}
            documentId={params.documentId!}
          />
        </div>
      </div>
      {FeatureFlags.aiChatEnabled() && !generateDescriptionState.isOpen && (
        <div className={styles.aiFabContainer}>
          <FabButton
            onClick={(e) => {
              e.stopPropagation();
              setGenerateDescriptionState({ isOpen: true });
            }}
            className={styles.aiChatButton}
            icon={<ChatIcon />}
          />
        </div>
      )}
      <GenerateDescriptionChat
        isOpen={generateDescriptionState.isOpen}
        onClose={() => setGenerateDescriptionState({ isOpen: false })}
        documentVariable={generateDescriptionState.documentVariable}
        projectId={params.projectId}
        revisionId={params.releaseId}
      />
    </ProjectProvider>
  );
};
